/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { OverflowCarousel } from '@coursera-web/cds-labs';

import {
  Button,
  Checkbox,
  CheckboxGroup,
  Typography2,
  VisuallyHidden,
  breakpoints,
  useMediaQuery,
} from '@coursera/cds-core';

import DegreeFilterChipDropdownsMobile from 'bundles/premium-hub/components/degrees/DegreeFilterChipDropdownsMobile';
import useDegreeFilterTracker from 'bundles/premium-hub/components/degrees/useDegreeFilterTracker';
import type { DegreeFilterState } from 'bundles/premium-hub/components/degrees/useMultiSelectDegreeFilterStateManager';
import type { PremiumHubPageType } from 'bundles/premium-hub/constants';
import { HubPages, REGIONAL_CATEGORIES } from 'bundles/premium-hub/constants';
import type { PremiumProductWithMetadata } from 'bundles/premium-hub/types';
import { getNameFromProductVariant } from 'bundles/premium-hub/utils';
import { FilterChipDropdown } from 'bundles/search-common/components/experiments/filters2024/FilterChipDropdown';

import _t from 'i18n!nls/premium-hub';

type Props = {
  degreeFilterState: DegreeFilterState;
  slug: string;
  categoryName?: string;
  currentPageType?: PremiumHubPageType;
  degrees: Array<PremiumProductWithMetadata>;
};

const styles = {
  wrapper: css`
    background-color: var(--cds-color-neutral-background-primary);
    padding: var(--cds-spacing-100) 0;
  `,
  filterTitle: css`
    padding-bottom: var(--cds-spacing-150);
  `,
  filterRow: css`
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: var(--cds-spacing-100);
  `,
  checkboxGroup: css`
    min-width: 240px;
  `,
  clearAllBtn: css`
    white-space: nowrap;
  `,
  noResults: css`
    padding: var(--cds-spacing-400) 0;
    text-align: center;
  `,
};

const getDegreeFilterTitle = (slug: string): string => {
  const titlesBySlug: Record<string, string> = {
    degrees_hub: _t('Find an online degree program that fits your goals'),
    bachelors: _t("Find the right online bachelor's degree"),
    masters: _t("Find the right online master's degree"),
    business: _t('Find the right online business degree'),
    'computer-science': _t('Find the right online computer science degree program'),
    'data-science': _t('Find the right online data science degree program'),
    'maestria-en-linea-latinoamerica': _t('Encuentra la maestría ideal para ti'),
  };
  return titlesBySlug[slug] ?? _t('Find the right degree for you');
};

const toggleValue = (values: string[], value: string, checked: boolean): string[] =>
  checked ? [...values, value] : values.filter((v) => v !== value);

type FacetOption = { id: string; name: string };

type FacetChipProps = {
  label: string;
  options: FacetOption[];
  selectedValues: string[];
  setSelected: (value: string | undefined) => void;
  setSubmitted: (value: string | undefined) => void;
  trackingName: string;
  testId?: string;
  onOpenAllFiltersPanel?: () => void;
  onChange?: (trackingName: string, values: string[]) => void;
};

const FacetChipDropdown = ({
  label,
  options,
  selectedValues,
  setSelected,
  setSubmitted,
  trackingName,
  testId,
  onOpenAllFiltersPanel,
  onChange,
}: FacetChipProps) => {
  const isMobileOrTabletView = useMediaQuery(breakpoints.down('sm'));

  // Applies immediately on every selection change, rather than waiting for the popover's
  // "View" button, so the filter bar behaves consistently whether a chip is opened or not.
  const commitValues = (values: string[]) => {
    const valueStr = values.length === 0 ? '' : values.join(',');
    if (onChange) {
      onChange(trackingName, values);
    }
    setSelected(valueStr);
    setSubmitted(valueStr);
  };

  return (
    <FilterChipDropdown
      triggerLabel={label}
      triggerCount={selectedValues.length}
      showCountThreshold={1}
      panelHeader={label}
      testId={testId}
      onApply={() => commitValues(selectedValues)}
      onReset={() => commitValues([])}
      onTriggerClick={isMobileOrTabletView && onOpenAllFiltersPanel ? () => onOpenAllFiltersPanel() : undefined}
    >
      <CheckboxGroup
        aria-label={label}
        value={selectedValues}
        css={styles.checkboxGroup}
        onChange={(e, checked) => {
          commitValues(toggleValue(selectedValues, e.target.value, checked));
        }}
      >
        {options.map(({ id, name }) => (
          <Checkbox key={id} label={name} value={id} />
        ))}
      </CheckboxGroup>
    </FilterChipDropdown>
  );
};

const DegreeFilterChipDropdowns = ({ degreeFilterState, slug, currentPageType, categoryName, degrees }: Props) => {
  const [isMobileDialogOpen, setIsMobileDialogOpen] = useState(false);
  const trackPerformSearch = useDegreeFilterTracker(degreeFilterState);

  const {
    availableProductVariantFilters = [],
    availableDomainFilters = [],
    availableCostFilters = [],
    availableDurationFilters = [],
    availableOpenContentAvailabilityFilters = [],
    selectedProductVariantValue = [],
    selectedDomainValue = [],
    selectedCostValue = [],
    selectedDurationValue = [],
    selectedOpenContentAvailabilityValue = [],
    setSelectedProductVariant,
    setSelectedDomain,
    setSelectedCost,
    setSelectedDuration,
    setSelectedOpenContentAvailability,
    setSubmittedProductVariant,
    setSubmittedDomain,
    setSubmittedCostBucket,
    setSubmittedDurationBucket,
    setSubmittedOpenContentAvailabilityBucket,
    submittedProductVariant,
    submittedDomain,
    submittedCostBucket,
    submittedDurationBucket,
    submittedOpenContentAvailabilityBucket,
    clearAll,
  } = degreeFilterState;

  const isLevelHub = currentPageType === HubPages.ProductVariantHubPage;
  const isCategoryHub = currentPageType === HubPages.DegreesCategoryHubPage;
  const isRegional = categoryName && REGIONAL_CATEGORIES.includes(categoryName);

  const hasFiltersSelected = Boolean(
    submittedProductVariant ||
    submittedDomain ||
    submittedCostBucket ||
    submittedDurationBucket ||
    submittedOpenContentAvailabilityBucket
  );

  // OverflowCarousel wraps every child (React.Children.map does not skip falsy
  // entries) in its own flex item, so a hidden `false` chip still consumes a
  // `gap` slot on both sides. Build the list ourselves and drop falsy entries
  // before rendering so hidden facets don't leave extra space behind.
  const filterChips = [
    !isLevelHub && (
      <FacetChipDropdown
        key="degree-level"
        label={_t('Degree Level')}
        options={availableProductVariantFilters.map((productVariant) => ({
          id: productVariant,
          name: getNameFromProductVariant(productVariant),
        }))}
        selectedValues={selectedProductVariantValue}
        setSelected={setSelectedProductVariant}
        setSubmitted={setSubmittedProductVariant}
        trackingName="productVariant"
        testId="degree-level-filter-chip"
        onOpenAllFiltersPanel={() => setIsMobileDialogOpen(true)}
        onChange={trackPerformSearch}
      />
    ),
    (!isCategoryHub || isRegional) && (
      <FacetChipDropdown
        key="area-of-study"
        label={_t('Area of Study')}
        options={availableDomainFilters}
        selectedValues={selectedDomainValue}
        setSelected={setSelectedDomain}
        setSubmitted={setSubmittedDomain}
        trackingName="domain"
        testId="area-of-study-filter-chip"
        onOpenAllFiltersPanel={() => setIsMobileDialogOpen(true)}
        onChange={trackPerformSearch}
      />
    ),
    <FacetChipDropdown
      key="cost"
      label={_t('Cost')}
      options={availableCostFilters}
      selectedValues={selectedCostValue}
      setSelected={setSelectedCost}
      setSubmitted={setSubmittedCostBucket}
      trackingName="cost"
      testId="cost-filter-chip"
      onOpenAllFiltersPanel={() => setIsMobileDialogOpen(true)}
      onChange={trackPerformSearch}
    />,
    <FacetChipDropdown
      key="duration"
      label={_t('Duration')}
      options={availableDurationFilters}
      selectedValues={selectedDurationValue}
      setSelected={setSelectedDuration}
      setSubmitted={setSubmittedDurationBucket}
      trackingName="duration"
      testId="duration-filter-chip"
      onOpenAllFiltersPanel={() => setIsMobileDialogOpen(true)}
      onChange={trackPerformSearch}
    />,
    <FacetChipDropdown
      key="open-content-availability"
      label={_t('Start with open content')}
      options={availableOpenContentAvailabilityFilters}
      selectedValues={selectedOpenContentAvailabilityValue}
      setSelected={setSelectedOpenContentAvailability}
      setSubmitted={setSubmittedOpenContentAvailabilityBucket}
      trackingName="openContentAvailability"
      testId="open-content-availability-filter-chip"
      onOpenAllFiltersPanel={() => setIsMobileDialogOpen(true)}
      onChange={trackPerformSearch}
    />,
    hasFiltersSelected && (
      <Button key="clear-all" variant="ghost" onClick={clearAll} size="small" css={styles.clearAllBtn}>
        {_t('Clear all')}
      </Button>
    ),
  ].filter(Boolean);

  return (
    <div css={styles.wrapper}>
      <Typography2 component="h2" variant="subtitleLarge" css={styles.filterTitle}>
        {getDegreeFilterTitle(slug)}
      </Typography2>
      <OverflowCarousel css={styles.filterRow}>{filterChips}</OverflowCarousel>
      <VisuallyHidden>
        <div role="status" aria-live="polite">
          {!degrees.length
            ? _t('No degrees match your filters. Try adjusting or clearing your filters to see more results.')
            : ''}
        </div>
      </VisuallyHidden>
      {!degrees.length && (
        <div css={styles.noResults} data-testid="degree-filters-redesign-no-results">
          <Typography2 component="p" variant="subtitleMedium">
            {_t('No degrees match your filters')}
          </Typography2>
          <Typography2 component="p" variant="bodySecondary">
            {_t('Try adjusting or clearing your filters to see more results.')}
          </Typography2>
        </div>
      )}
      <DegreeFilterChipDropdownsMobile
        open={isMobileDialogOpen}
        handleClose={() => setIsMobileDialogOpen(false)}
        degreeFilterState={degreeFilterState}
        categoryName={categoryName}
        currentPageType={currentPageType}
      />
    </div>
  );
};

export default React.memo(DegreeFilterChipDropdowns);
